import React, { useCallback, useEffect, useState, useMemo } from "react";
import SummaryCard from "./summary-doc-card";
import HistoryTabs from "./vehicle-doc-tab";
import HistoryTable from "./vehicle-doc-table";
import { getVehicleNoAPI, refreshVehicleHistoryAPI } from "../vehicle_api";
import ReactPaginate from "react-paginate";
import { Dropdown, Spinner, Button } from "react-bootstrap";
import down from "../../../../assets/Vehicles/chevron_down.png";
import lar from "../../../../assets/svg/lar.svg";
import rar from "../../../../assets/svg/rar.svg";

const PAGE_SIZES = [10, 25, 50, 75, 100];

const VehicleDocumentsPage = ({ vehicleId, refreshKey, licensePlate, handleClose, isParivahan, onUpdateSuccess }) => {
  const [activeTab, setActiveTab] = useState("RC");
  const [vehicleDetails, setVehicleDetails] = useState({});
  const [historyList, setHistoryList] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);

  // --- Handlers ---
  const handlePageClick = useCallback((event) => {
    setItemOffset(event.selected);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setItemsPerPage(size);
    setItemOffset(0);
  }, []);

  const getRcDetails = useCallback(async () => {
    if (!vehicleId) return;

    setLoading(true);
    try {
      const param = {
        type: activeTab.toLocaleUpperCase(),
        page: itemOffset + 1,
        limit: itemsPerPage,
      };

      const response = await getVehicleNoAPI(vehicleId, param);

      if (response?.data?.success === true) {
        setVehicleDetails(response.data.summary ?? {});
        setHistoryList(response.data.histories ?? []);
        setPageCount(response.data.total_pgs ?? 0);
      }
    } catch (err) {
      console.error("Error fetching category:", err);
    } finally {
      setLoading(false);
    }
  }, [vehicleId, activeTab, itemOffset, itemsPerPage]);

  // --- Effects ---
  useEffect(() => {
    getRcDetails();
  }, [getRcDetails]);

  // Refresh when refreshKey changes (triggered by parent)
  useEffect(() => {
    if (refreshKey) {
      getRcDetails();
    }
  }, [refreshKey]);

  const summaryCards = useMemo(
    () => (
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <SummaryCard title="RC Info" mainText={vehicleDetails.rc_number ?? "-"} expiry={vehicleDetails.rc_valid_upto ?? "-"} status={vehicleDetails.rc_status ?? "-"} />
        </div>

        <div className="col-md-4">
          <SummaryCard title="Insurance Info" mainText={vehicleDetails.insurance_company ?? "-"} expiry={vehicleDetails.insurance_upto ?? "-"} status={vehicleDetails.insurance_status ?? "-"} />
        </div>

        <div className="col-md-4">
          <SummaryCard title="Permit Info" mainText={vehicleDetails.permit_number ?? "-"} expiry={vehicleDetails.permit_valid_upto ?? "-"} status={vehicleDetails.permit_status ?? "-"} />
        </div>
      </div>
    ),
    [vehicleDetails],
  );

  return (
    <div className="bg-white rounded-4 p-3 w-100">
      {/* Page Title */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="fw-600 fs-16">RC, Insurance & Permit details</h4>
        {isParivahan && (
          <Button
            className="dark-purple-btn rounded-pill py-2 px-4"
            disabled={statusLoading}
            onClick={async () => {
              setStatusLoading(true);
              try {
                const response = await refreshVehicleHistoryAPI(licensePlate);
                if (handleClose) handleClose(response);
                if (response.data?.success) {
                  if (onUpdateSuccess) onUpdateSuccess();
                }
              } catch (err) {
                console.error("Error updating status:", err);
                if (handleClose) handleClose({ data: { success: false, message: "Failed to update status" } });
              } finally {
                setStatusLoading(false);
              }
            }}
          >
            {statusLoading ? <Spinner animation="border" size="sm" style={{ color: "#FFFFFF" }} /> : "Update Current Status"}
          </Button>
        )}
      </div>
      {/* Top Summary Cards */}
      {summaryCards}

      {/* Tabs */}
      <HistoryTabs activeTab={activeTab} onChange={setActiveTab} />

      {/* Loading Spinner */}
      {loading ? (
        <div className="d-flex justify-content-center align-items-center py-5">
          <Spinner animation="border" style={{ color: "#471070" }} />
        </div>
      ) : (
        <>
          {/* Table */}
          <HistoryTable activeTab={activeTab} historyList={historyList} />

          <div className="py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
            <Dropdown>
              <span className="pagination-color">Records per page: &nbsp;</span>
              <Dropdown.Toggle id="dropdown-basic" className="bg-transparent pagination-color border-0 custom-dropdown-toggle p-0">
                <span className="bg-white no-of-rows rounded-3 px-2 py-2">
                  {itemsPerPage} <img src={down} alt="" />
                </span>
              </Dropdown.Toggle>
              <Dropdown.Menu className="my-2" style={{ minWidth: "unset", width: "30%" }}>
                {PAGE_SIZES.map((size) => (
                  <Dropdown.Item key={size} onClick={() => handlePageSizeChange(size)} active={itemsPerPage === size} className="text-center">
                    {size}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>

            <div className="d-flex align-items-center gap-2">
              <ReactPaginate previousLabel={<img src={lar} className="ar3" alt="previous" />} nextLabel={<img src={rar} className="ar3" alt="next" />} breakLabel={"..."} pageCount={pageCount + 1} marginPagesDisplayed={2} pageRangeDisplayed={3} onPageChange={handlePageClick} containerClassName={"pagination"} activeClassName={"active"} />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VehicleDocumentsPage;
